// Floating launcher placement + optional user-drag.
//
// Custom (dragged) positions anchor the root by viewport `bottom` plus
// `left` or `right`, so the launcher corner stays fixed while the panel
// grows upward (column-reverse). Open/close never rewrites coordinates.

import type { LauncherPosition } from './config'
import type { FrameRefs } from './frame'

const POSITION_CLASS_PREFIX = 'se-pos-'

const STORAGE_KEY_LEFT = 'aevo_launcher_x'
const STORAGE_KEY_BOTTOM = 'aevo_launcher_bottom'
const STORAGE_KEY_RIGHT = 'aevo_launcher_right'
/** @deprecated Legacy top anchor; migrated on load */
const STORAGE_KEY_TOP_LEGACY = 'aevo_launcher_y'

const VIEWPORT_MARGIN = 8
const LAUNCHER_FALLBACK_PX = 56

const POSITION_CLASSES: Record<LauncherPosition, string> = {
  'top-left': `${POSITION_CLASS_PREFIX}top-left`,
  'top-center': `${POSITION_CLASS_PREFIX}top-center`,
  'top-right': `${POSITION_CLASS_PREFIX}top-right`,
  'center-left': `${POSITION_CLASS_PREFIX}center-left`,
  'center-right': `${POSITION_CLASS_PREFIX}center-right`,
  'bottom-left': `${POSITION_CLASS_PREFIX}bottom-left`,
  'bottom-center': `${POSITION_CLASS_PREFIX}bottom-center`,
  'bottom-right': `${POSITION_CLASS_PREFIX}bottom-right`,
}

type AnchorSide = 'left' | 'right'

interface BottomAnchor {
  bottom: number
  left?: number
  right?: number
  side: AnchorSide
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

function applyPositionClass(root: HTMLElement, position: LauncherPosition): void {
  for (const cls of Object.values(POSITION_CLASSES)) root.classList.remove(cls)
  root.classList.add(POSITION_CLASSES[position])
}

export function hasCustomLauncherPosition(root: HTMLElement): boolean {
  return root.classList.contains('is-custom-position')
}

/**
 * Which corner of the viewport the launcher is anchored to, or null for
 * preset CSS-class anchors. Resize handle position derives from this so
 * the grab corner is always opposite the fixed anchor (== the only
 * corner that can grow both axes naturally).
 */
export function getCustomAnchorSide(root: HTMLElement): AnchorSide | null {
  if (!hasCustomLauncherPosition(root)) return null
  if (root.classList.contains('se-anchor-br')) return 'right'
  if (root.classList.contains('se-anchor-bl')) return 'left'
  return null
}

function markCustomPosition(root: HTMLElement): void {
  root.classList.add('is-custom-position')
}

function anchorSideFromLauncher(launcher: HTMLElement): AnchorSide {
  const rect = launcher.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  return centerX > window.innerWidth / 2 ? 'right' : 'left'
}

function applyBottomAnchor(root: HTMLElement, anchor: BottomAnchor): void {
  markCustomPosition(root)
  root.style.top = 'auto'
  root.style.transform = 'none'
  root.classList.remove('se-anchor-bl', 'se-anchor-br')
  root.style.bottom = `${anchor.bottom}px`

  if (anchor.side === 'right' && anchor.right != null) {
    root.style.right = `${anchor.right}px`
    root.style.left = 'auto'
    root.classList.add('se-anchor-br')
  } else {
    root.style.left = `${anchor.left ?? VIEWPORT_MARGIN}px`
    root.style.right = 'auto'
    root.classList.add('se-anchor-bl')
  }
}

function readBottomAnchorFromRoot(root: HTMLElement): BottomAnchor {
  const rect = root.getBoundingClientRect()
  const bottom = window.innerHeight - rect.bottom
  if (root.classList.contains('se-anchor-br')) {
    return {
      side: 'right',
      right: window.innerWidth - rect.right,
      bottom,
    }
  }
  return {
    side: 'left',
    left: rect.left,
    bottom,
  }
}

function saveBottomAnchor(anchor: BottomAnchor): void {
  safeSetItem(STORAGE_KEY_BOTTOM, String(Math.round(anchor.bottom)))
  try {
    localStorage.removeItem(STORAGE_KEY_TOP_LEGACY)
  } catch {
    /* ignore */
  }
  if (anchor.side === 'right' && anchor.right != null) {
    safeSetItem(STORAGE_KEY_RIGHT, String(Math.round(anchor.right)))
    safeSetItem(STORAGE_KEY_LEFT, '')
  } else {
    safeSetItem(STORAGE_KEY_LEFT, String(Math.round(anchor.left ?? VIEWPORT_MARGIN)))
    safeSetItem(STORAGE_KEY_RIGHT, '')
  }
}

function loadSavedBottomAnchor(launcher: HTMLElement): BottomAnchor | null {
  const bottomRaw = safeGetItem(STORAGE_KEY_BOTTOM)
  if (bottomRaw != null) {
    const bottom = Number(bottomRaw)
    if (!Number.isFinite(bottom)) return null
    const rightRaw = safeGetItem(STORAGE_KEY_RIGHT)
    if (rightRaw != null && rightRaw !== '') {
      const right = Number(rightRaw)
      if (Number.isFinite(right)) {
        return { side: 'right', right, bottom }
      }
    }
    const leftRaw = safeGetItem(STORAGE_KEY_LEFT)
    const left = leftRaw != null ? Number(leftRaw) : NaN
    if (Number.isFinite(left)) {
      return { side: 'left', left, bottom }
    }
    return null
  }

  // Migrate legacy top/left saves to bottom anchor.
  const leftRaw = safeGetItem(STORAGE_KEY_LEFT)
  const topRaw = safeGetItem(STORAGE_KEY_TOP_LEGACY)
  if (leftRaw == null || topRaw == null || topRaw === '') return null
  const left = Number(leftRaw)
  const top = Number(topRaw)
  if (!Number.isFinite(left) || !Number.isFinite(top)) return null
  const h = launcher.offsetHeight || LAUNCHER_FALLBACK_PX
  const bottom = window.innerHeight - top - h
  const side = anchorSideFromLauncher(launcher)
  if (side === 'right') {
    const w = launcher.offsetWidth || LAUNCHER_FALLBACK_PX
    return {
      side: 'right',
      right: window.innerWidth - left - w,
      bottom,
    }
  }
  return { side: 'left', left, bottom }
}

function measureClampSize(root: HTMLElement, launcher: HTMLElement): { w: number; h: number } {
  const lw = Math.max(40, launcher.offsetWidth || LAUNCHER_FALLBACK_PX)
  const lh = Math.max(40, launcher.offsetHeight || LAUNCHER_FALLBACK_PX)
  if (root.classList.contains('is-collapsed')) return { w: lw, h: lh }
  return {
    w: Math.max(lw, root.offsetWidth || lw),
    h: Math.max(lh, root.offsetHeight || lh),
  }
}

function clampBottomAnchor(
  anchor: BottomAnchor,
  root: HTMLElement,
  launcher: HTMLElement,
): BottomAnchor {
  const { w, h } = measureClampSize(root, launcher)
  const margin = VIEWPORT_MARGIN
  const vh = window.innerHeight
  const vw = window.innerWidth
  const bottom = Math.max(margin, Math.min(vh - h - margin, anchor.bottom))

  if (anchor.side === 'right' && anchor.right != null) {
    const right = Math.max(margin, Math.min(vw - w - margin, anchor.right))
    return { side: 'right', right, bottom }
  }

  const left = Math.max(margin, Math.min(vw - w - margin, anchor.left ?? margin))
  return { side: 'left', left, bottom }
}

/**
 * Max panel height (px) for bottom-anchored custom positions. Returns null
 * when the preset anchor layout applies. Used by resize.ts instead of an
 * inline max-height, which would block vertical resizing.
 *
 * Derived from the *fixed* bottom anchor (not the current root rect) so
 * the value is stable while the user is dragging the resize handle —
 * reading the live rect during resize creates a feedback loop because
 * the root grows upward as the panel grows, which would shrink
 * `spaceAbove` and clamp the height back down on the next tick.
 */
export function getCustomPanelMaxHeightPx(refs: FrameRefs): number | null {
  if (!hasCustomLauncherPosition(refs.root)) return null
  if (refs.root.classList.contains('is-collapsed')) return null

  const bottomPx = parsePxStyle(refs.root.style.bottom, VIEWPORT_MARGIN)
  const panelBottomY = window.innerHeight - bottomPx
  const available = panelBottomY - VIEWPORT_MARGIN
  return Math.max(320, Math.floor(available))
}

function parsePxStyle(value: string, fallback: number): number {
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Before open: stack panel above launcher. Does not change coordinates.
 */
export function preparePanelOpenLayout(refs: FrameRefs): void {
  if (!hasCustomLauncherPosition(refs.root)) return
  refs.root.classList.add('se-stack-up')
}

/** After paint: shrink height once if the panel clips the top (no max-height). */
export function finalizePanelOpenLayout(refs: FrameRefs): void {
  fitPanelHeightToViewport(refs)
}

/**
 * After close: remove expansion-only classes. Anchor is unchanged so the
 * launcher returns to the same viewport corner.
 */
export function restoreCollapsedLauncherLayout(refs: FrameRefs): void {
  if (!hasCustomLauncherPosition(refs.root)) return
  refs.root.classList.remove('se-stack-up')
}

/** If the panel top is above the viewport, reduce height — anchor stays fixed. */
function fitPanelHeightToViewport(refs: FrameRefs): void {
  if (!hasCustomLauncherPosition(refs.root)) return
  if (refs.root.classList.contains('is-collapsed')) return

  const margin = VIEWPORT_MARGIN
  const rootRect = refs.root.getBoundingClientRect()
  if (rootRect.top >= margin) return

  const panelRect = refs.panel.getBoundingClientRect()
  const overflow = margin - rootRect.top
  const nextH = Math.max(320, Math.floor(panelRect.height - overflow))
  refs.panel.style.height = `${nextH}px`
}

/** Undo a dragged position and the draggable affordance (before re-attaching). */
export function clearCustomPosition(root: HTMLElement): void {
  root.classList.remove(
    'is-custom-position',
    'is-draggable',
    'is-dragging',
    'se-anchor-bl',
    'se-anchor-br',
    'se-stack-up',
  )
  for (const prop of ['top', 'left', 'right', 'bottom', 'transform'] as const) root.style[prop] = ''
}

/**
 * Apply the chosen launcher position class and (optionally) wire pointer
 * drag. Returns a disposer that removes drag listeners. `persist: false`
 * (the studio preview) neither restores nor saves a dragged position.
 */
export function attachLauncherPosition(
  refs: FrameRefs,
  opts: { position: LauncherPosition; draggable: boolean; persist?: boolean },
): () => void {
  clearCustomPosition(refs.root)
  applyPositionClass(refs.root, opts.position)

  if (!opts.draggable) {
    return () => {
      /* nothing to clean up */
    }
  }

  refs.root.classList.add('is-draggable')
  const persist = opts.persist !== false

  const saved = persist ? loadSavedBottomAnchor(refs.launcher) : null
  if (saved) {
    applyBottomAnchor(
      refs.root,
      clampBottomAnchor(saved, refs.root, refs.launcher),
    )
  }

  let pointerId: number | null = null
  let startClientX = 0
  let startClientY = 0
  let startAnchor: BottomAnchor | null = null
  let dragging = false

  const onPointerDown = (e: PointerEvent): void => {
    if (e.button !== undefined && e.button !== 0) return
    if (!refs.root.classList.contains('is-collapsed')) return

    pointerId = e.pointerId
    dragging = false
    startClientX = e.clientX
    startClientY = e.clientY
    startAnchor = readBottomAnchorFromRoot(refs.root)
    refs.launcher.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: PointerEvent): void => {
    if (pointerId === null || pointerId !== e.pointerId || !startAnchor) return
    const dx = e.clientX - startClientX
    const dy = e.clientY - startClientY
    if (!dragging) {
      if (Math.hypot(dx, dy) < 4) return
      dragging = true
      refs.root.classList.add('is-dragging')
      refs.launcher.classList.add('is-dragging')
    }

    let next: BottomAnchor
    if (startAnchor.side === 'right' && startAnchor.right != null) {
      next = {
        side: 'right',
        right: startAnchor.right - dx,
        bottom: startAnchor.bottom - dy,
      }
    } else {
      next = {
        side: 'left',
        left: (startAnchor.left ?? VIEWPORT_MARGIN) + dx,
        bottom: startAnchor.bottom - dy,
      }
    }

    applyBottomAnchor(
      refs.root,
      clampBottomAnchor(next, refs.root, refs.launcher),
    )
  }

  const finishDrag = (e: PointerEvent): void => {
    if (pointerId === null || pointerId !== e.pointerId) return
    if (refs.launcher.hasPointerCapture(e.pointerId)) {
      refs.launcher.releasePointerCapture(e.pointerId)
    }
    pointerId = null
    if (!dragging) return
    dragging = false
    refs.root.classList.remove('is-dragging')
    refs.launcher.classList.remove('is-dragging')

    const side = anchorSideFromLauncher(refs.launcher)
    const rect = refs.root.getBoundingClientRect()
    const anchor: BottomAnchor = {
      side,
      bottom: window.innerHeight - rect.bottom,
      ...(side === 'right'
        ? { right: window.innerWidth - rect.right }
        : { left: rect.left }),
    }
    if (persist) saveBottomAnchor(anchor)
    applyBottomAnchor(refs.root, clampBottomAnchor(anchor, refs.root, refs.launcher))

    refs.launcher.addEventListener(
      'click',
      (ev) => {
        ev.preventDefault()
        ev.stopImmediatePropagation()
      },
      { capture: true, once: true },
    )
  }

  const onWindowResize = (): void => {
    if (!hasCustomLauncherPosition(refs.root)) return
    const anchor = readBottomAnchorFromRoot(refs.root)
    applyBottomAnchor(
      refs.root,
      clampBottomAnchor(anchor, refs.root, refs.launcher),
    )
    if (!refs.root.classList.contains('is-collapsed')) {
      fitPanelHeightToViewport(refs)
    }
  }

  refs.launcher.addEventListener('pointerdown', onPointerDown)
  refs.launcher.addEventListener('pointermove', onPointerMove)
  refs.launcher.addEventListener('pointerup', finishDrag)
  refs.launcher.addEventListener('pointercancel', finishDrag)
  window.addEventListener('resize', onWindowResize)

  return () => {
    refs.launcher.removeEventListener('pointerdown', onPointerDown)
    refs.launcher.removeEventListener('pointermove', onPointerMove)
    refs.launcher.removeEventListener('pointerup', finishDrag)
    refs.launcher.removeEventListener('pointercancel', finishDrag)
    window.removeEventListener('resize', onWindowResize)
  }
}
